import React, { useState, useEffect, useCallback } from 'react';
import { healthApi } from '../services/api';
import { formatTimestamp } from '../utils/format';

function StatusDot({ status }) {
    const colors = {
        CONNECTED: 'bg-green-500',
        RUNNING: 'bg-green-500',
        HEALTHY: 'bg-green-500',
        DEGRADED: 'bg-yellow-500',
        STOPPED: 'bg-gray-400',
        DISCONNECTED: 'bg-red-500',
        FAILED: 'bg-red-500',
        ERROR: 'bg-red-500',
        DEAD: 'bg-red-500',
        UNREACHABLE: 'bg-red-500',
        UNKNOWN: 'bg-gray-300',
        CRITICAL: 'bg-red-500',
    };
    return (
        <span className={`inline-block w-3 h-3 rounded-full ${colors[status] || 'bg-gray-300'}`} />
    );
}

function StatusBadge({ status }) {
    const colors = {
        CONNECTED: 'bg-green-100 text-green-800',
        RUNNING: 'bg-green-100 text-green-800',
        HEALTHY: 'bg-green-100 text-green-800',
        DEGRADED: 'bg-yellow-100 text-yellow-800',
        STOPPED: 'bg-gray-100 text-gray-800',
        DISCONNECTED: 'bg-red-100 text-red-800',
        FAILED: 'bg-red-100 text-red-800',
        ERROR: 'bg-red-100 text-red-800',
        DEAD: 'bg-red-100 text-red-800',
        UNREACHABLE: 'bg-red-100 text-red-800',
        UNKNOWN: 'bg-gray-100 text-gray-600',
        CRITICAL: 'bg-red-100 text-red-800',
    };
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
            {status}
        </span>
    );
}

function ComponentCard({ name, status, details }) {
    return (
        <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium capitalize">{name}</h3>
                <StatusBadge status={status} />
            </div>
            {details && (
                <div className="text-sm text-gray-600 space-y-1">
                    {Object.entries(details).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                            <span className="text-gray-500">{key}:</span>
                            <span className="font-mono text-xs">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ProjectHealthCard({ name, data }) {
    return (
        <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">{name}</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center space-x-2">
                    <StatusDot status={data.consumerStatus} />
                    <span>Consumer</span>
                    <span className="text-gray-500">{data.consumerStatus}</span>
                </div>
                <div className="flex items-center space-x-2">
                    <StatusDot status={data.connectorStatus} />
                    <span>Connector</span>
                    <span className="text-gray-500">{data.connectorStatus}</span>
                </div>
                <div className="col-span-2 text-gray-500">
                    Buffer: {data.bufferSize || 0} transactions
                </div>
            </div>
        </div>
    );
}

function SystemEventRow({ event }) {
    const isError = event.status === 'FAILED';
    const [showDetail, setShowDetail] = useState(false);
    return (
        <tr className="border-b hover:bg-gray-50">
            <td className="px-4 py-2 text-sm">{formatTimestamp(event.timestamp)}</td>
            <td className="px-4 py-2 text-sm font-medium">{event.projectName || '-'}</td>
            <td className="px-4 py-2">
                <span className={`px-2 py-1 rounded text-xs font-mono ${isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                    {event.operation}
                </span>
            </td>
            <td className="px-4 py-2">
                <StatusBadge status={event.status} />
            </td>
            <td className="px-4 py-2 text-sm text-gray-600 max-w-md relative"
                onMouseEnter={() => setShowDetail(true)}
                onMouseLeave={() => setShowDetail(false)}>
                <span className="block truncate">{event.detail}</span>
                {showDetail && event.detail && (
                    <div style={{position:'absolute', left:0, top:'100%', paddingTop:'4px', zIndex:9999}}
                         onMouseEnter={() => setShowDetail(true)}
                         onMouseLeave={() => setShowDetail(false)}>
                        <div style={{background:'#1f2937', color:'white', padding:'12px', borderRadius:'6px', fontSize:'13px', minWidth:'400px', maxWidth:'700px', whiteSpace:'normal', wordBreak:'break-word', boxShadow:'0 10px 15px -3px rgba(0,0,0,0.3)', userSelect:'all'}}>
                            {event.detail}
                        </div>
                    </div>
                )}
            </td>
        </tr>
    );
}

function getDebeziumStatus(connectors) {
    if (!connectors || Object.keys(connectors).length === 0) return 'UNKNOWN';
    const states = Object.values(connectors);
    if (states.includes('FAILED')) return 'FAILED';
    if (states.includes('UNREACHABLE')) return 'UNREACHABLE';
    if (states.includes('DEGRADED')) return 'DEGRADED';
    if (states.every(s => s === 'RUNNING')) return 'RUNNING';
    return 'UNKNOWN';
}

function HealthDashboard() {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadHealth = useCallback(async () => {
        try {
            const res = await healthApi.getGlobalHealth();
            setHealth(res.data);
            setError(null);
        } catch (err) {
            setError('Failed to load health status');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadHealth();
        const interval = setInterval(loadHealth, 10000);
        return () => clearInterval(interval);
    }, [loadHealth]);

    if (loading) {
        return <div className="text-center py-12 text-gray-500">Loading health status...</div>;
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-red-600">{error}</div>
                <button onClick={loadHealth} className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                    Retry
                </button>
            </div>
        );
    }

    const components = health?.components || {};
    const projects = health?.projects || {};
    const events = health?.recentSystemEvents || [];
    const debeziumStatus = getDebeziumStatus(components.debezium?.connectors);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Architecture Health</h1>
                <div className="flex items-center space-x-2">
                    <StatusDot status={health?.status} />
                    <span className="font-medium">{health?.status}</span>
                    <span className="text-sm text-gray-500">Last check: {formatTimestamp(health?.timestamp)}</span>
                </div>
            </div>

            {/* Component Status */}
            <div>
                <h2 className="text-lg font-medium mb-3">Components</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ComponentCard name="Kafka" status={components.kafka?.status} details={components.kafka} />
                    <ComponentCard name="MongoDB" status={components.mongodb?.status} details={components.mongodb} />
                    <ComponentCard name="Debezium" status={debeziumStatus} details={components.debezium} />
                </div>
            </div>

            {/* Project Status */}
            <div>
                <h2 className="text-lg font-medium mb-3">Projects</h2>
                {Object.keys(projects).length === 0 ? (
                    <div className="text-gray-500">No projects configured</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(projects).map(([name, data]) => (
                            <ProjectHealthCard key={name} name={name} data={data} />
                        ))}
                    </div>
                )}
            </div>

            {/* System Events */}
            <div>
                <h2 className="text-lg font-medium mb-3">Recent System Events</h2>
                <div className="bg-white rounded-lg shadow">
                    {events.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">No system events recorded</div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                                    <th className="px-4 py-2">Time</th>
                                    <th className="px-4 py-2">Project</th>
                                    <th className="px-4 py-2">Event</th>
                                    <th className="px-4 py-2">Status</th>
                                    <th className="px-4 py-2">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((event, idx) => (
                                    <SystemEventRow key={idx} event={event} />
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default HealthDashboard;
